import { useState } from 'react'
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import type { inferInput, inferOutput } from '@trpc/tanstack-react-query'
import { Alert, App, Button, Form, Input, Modal, Skeleton, Table } from 'antd'
import { useTRPC } from '@/trpc/react.ts'
import { formatAmount } from '@/utils/money.ts'
import type { GroupAction } from './group-actions.tsx'
import { refreshMoneyQueries } from './utils/refresh-money-queries.ts'

type Procedures = ReturnType<typeof useTRPC>['bill']
export type GroupBatchTarget = inferInput<Procedures['groupBatchTargets']>
type Group = inferOutput<Procedures['groupBatchTargets']>[number]
export function GroupBatchDialog({
  target,
  action,
  onClose
}: {
  target: GroupBatchTarget
  action: Exclude<GroupAction, 'receipt'>
  onClose: () => void
}) {
  const trpc = useTRPC()
  const busy = useIsMutating({ mutationKey: trpc.bill.groupBatchAction.mutationOptions().mutationKey }) > 0
  const query = useQuery({
    ...trpc.bill.groupBatchTargets.queryOptions(target),
    staleTime: 0,
    refetchOnWindowFocus: false
  })
  return (
    <Modal
      open
      title={action === 'print' ? '批量打印收据' : '批量作废并释放账单'}
      width="64rem"
      footer={null}
      closable={!busy}
      mask={{ closable: !busy }}
      onCancel={() => {
        if (!busy) {
          onClose()
        }
      }}
      destroyOnHidden
    >
      {query.isError ? (
        <Alert
          type="error"
          title={query.error.message}
          action={<Button onClick={() => void query.refetch()}>重试</Button>}
        />
      ) : query.isFetchedAfterMount && query.data ? (
        <GroupBatchReview groups={query.data} action={action} onClose={onClose} />
      ) : (
        <Skeleton active />
      )}
    </Modal>
  )
}
function GroupBatchReview({
  groups,
  action,
  onClose
}: {
  groups: Group[]
  action: Exclude<GroupAction, 'receipt'>
  onClose: () => void
}) {
  const trpc = useTRPC(),
    cache = useQueryClient(),
    router = useRouter(),
    { message } = App.useApp()
  // 预览集合固定为当前会话快照，后台刷新不改变将要提交的目标。
  const [snapshot] = useState(groups)
  const eligible = snapshot.filter((group) =>
    action === 'print' ? group.status !== 'draft' : group.status !== 'voided'
  )
  const [form] = Form.useForm<{ reason: string }>()
  const mutation = useMutation(trpc.bill.groupBatchAction.mutationOptions())
  const [result, setResult] = useState<inferOutput<Procedures['groupBatchAction']>>()
  const [submitted, setSubmitted] = useState<inferInput<Procedures['groupBatchAction']>>()
  const refresh = async () => {
    await refreshMoneyQueries(cache, trpc)
  }
  const submit = async () => {
    try {
      let input = submitted
      if (!input) {
        const values = await form.validateFields()
        input = {
          action: 'void',
          reason: values.reason,
          items: eligible.map((group) => ({ groupId: group.id, expectedUpdatedAt: group.updatedAt }))
        }
        setSubmitted(input)
      }
      setResult(await mutation.mutateAsync(input))
      await refresh()
    } catch (error) {
      if (error instanceof Error) {
        void message.error(error.message)
      } else if (typeof error === 'object' && error && 'errorFields' in error) {
        const fields = error.errorFields as { name: (string | number)[] }[]
        if (fields.length) {
          form.scrollToField(fields[0].name, { focus: true })
        }
      }
    }
  }
  const href = router.buildLocation({ to: '/finance/receipts', search: { ids: eligible.map((g) => g.id) } }).href
  return (
    <div className="space-y-4">
      <Alert
        showIcon
        type="info"
        title={`命中 ${snapshot.length} 个账单组，可处理 ${eligible.length} 个，跳过 ${snapshot.length - eligible.length} 个`}
        description={
          action === 'void'
            ? '仅释放组单与账单的关联，不撤销已有资金流水；已作废组会跳过。'
            : '草稿不生成正式收据；已作废收据会显示作废标记。'
        }
      />
      <Form form={form} layout="vertical" disabled={mutation.isPending || !!submitted}>
        <Table<Group>
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ x: 'max-content', y: '24rem' }}
          dataSource={eligible}
          columns={[
            { title: '账单组', dataIndex: 'groupNo' },
            { title: '客户', dataIndex: 'customerName' },
            { title: '待收金额', dataIndex: 'outstandingAmount', render: formatAmount, align: 'right' }
          ]}
        />
        {action === 'void' && (
          <Form.Item name="reason" label="作废原因" rules={[{ required: true, whitespace: true }]}>
            <Input.TextArea maxLength={1000} />
          </Form.Item>
        )}
      </Form>
      {result && (
        <Alert
          type={result.some((r) => !r.success) ? 'warning' : 'success'}
          title={`成功 ${result.filter((r) => r.success).length} 项，失败 ${result.filter((r) => !r.success).length} 项`}
          description={result
            .filter((r) => !r.success)
            .map((r) => (
              <p key={r.groupId}>
                组 ID {r.groupId}：{r.message}
              </p>
            ))}
        />
      )}
      <div className="flex justify-end gap-3">
        <Button onClick={onClose} disabled={mutation.isPending}>
          关闭
        </Button>
        {action === 'print' ? (
          <Button type="primary" href={href} target="_blank" rel="noopener noreferrer" disabled={!eligible.length}>
            打开收据工作台
          </Button>
        ) : (
          <Button
            type="primary"
            danger
            loading={mutation.isPending}
            disabled={!eligible.length || !!result}
            onClick={() => void submit()}
          >
            {submitted ? '重试原提交' : '确认作废并释放账单'}
          </Button>
        )}
      </div>
    </div>
  )
}
